import { useEffect, useRef } from "react";
import { Cloud, CloudOff, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  useOnlineStatus,
  usePendingCount,
  processPendingOps,
} from "@/lib/offlineQueue";

/**
 * Fixed top banner: shows "Offline" when no network or when there are pending
 * operations waiting to be synced. Triggers automatic sync on reconnect.
 */
export function OnlineStatusBar() {
  const online = useOnlineStatus();
  const { count, refresh } = usePendingCount();
  const wasOffline = useRef(!online);

  // Auto-sync when coming back online or on app start if there is a backlog
  useEffect(() => {
    let cancelled = false;
    const trySync = async () => {
      if (!online) return;
      const r = await processPendingOps();
      if (cancelled) return;
      refresh();
      if (r.synced > 0) {
        toast.success(
          `${r.synced} Änderung${r.synced === 1 ? "" : "en"} synchronisiert`,
          { icon: <CheckCircle2 className="text-[#00E575]" size={18} /> }
        );
      }
      if (r.failed > 0) {
        toast.error(
          `${r.failed} Operation${r.failed === 1 ? "" : "en"} konnten nicht gesynct werden`
        );
      }
    };
    if (online) trySync();
    return () => { cancelled = true; };
  }, [online, refresh]);

  // Toast on connectivity change
  useEffect(() => {
    if (!online && !wasOffline.current) {
      toast.warning("Offline – Änderungen werden zwischengespeichert", {
        icon: <CloudOff size={18} />,
      });
      wasOffline.current = true;
    } else if (online && wasOffline.current) {
      toast.success("Wieder online", { icon: <Cloud size={18} /> });
      wasOffline.current = false;
    }
  }, [online]);

  // Nothing to show: online and no backlog
  if (online && count === 0) return null;

  const offline = !online;

  return (
    <div
      data-testid="online-status-bar"
      className={`fixed top-0 left-0 right-0 z-[60] px-3 py-2 text-[12.5px] font-semibold flex items-center justify-center gap-2 ${
        offline
          ? "bg-amber-500/95 text-[#09090B]"
          : "bg-[#00E575]/95 text-[#09090B]"
      }`}
    >
      {offline ? (
        <>
          <CloudOff size={16} />
          <span data-testid="offline-status-text">
            Offline-Modus aktiv
            {count > 0 && <> · {count} Änderung{count === 1 ? "" : "en"} in der Warteschlange</>}
          </span>
        </>
      ) : (
        <>
          <RefreshCw size={16} className="animate-spin" />
          <span data-testid="syncing-status-text">
            Synchronisiere {count} Änderung{count === 1 ? "" : "en"}…
          </span>
        </>
      )}
    </div>
  );
}
